import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import {
  TrainingsSessionScheduleEnum,
  TrainingsSessionUpdateStrategyEnum,
} from 'generated/resources/graphql'
import moment from 'moment'

import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { ReminderValues } from 'modules/trainings/components/NewReminder/useNewReminder'
import { ScheduledEvent } from 'modules/trainings/components/Revamp/NewClassWizard/EventDataStep/EventBox/Scheduled'
import { SelfPacedEvent } from 'modules/trainings/components/Revamp/NewClassWizard/EventDataStep/EventBox/SelfPaced'
import { SessionData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { useSessionsForm } from 'modules/trainings/hooks/Sessions/useSessionsForm'
import { useIsRelatorioEnabled } from 'modules/trainings/hooks/Settings/settingUtils'
import { useValidation } from 'modules/trainings/hooks/useValidation'
import CalendarInvitesCard from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/SessionsTab/NewSessionModal/CalendarInvitesCard'
import ReminderCard from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/SessionsTab/NewSessionModal/ReminderCard'
import { SessionSelectStrategyModal } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/SessionsTab/SessionsDatatable/SessionSelectStrategyModal'

interface EventBoxProps {
  trainingId: string
  trainingClassId: string
  initialData?: SessionData
  onClose: () => void
  onSave: (session: SessionData) => void
  groupStart: string
  groupEnd: string
  border?: boolean
  featureFlag?: boolean
}
export const EventBox = ({
  trainingId,
  trainingClassId,
  initialData,
  onClose,
  onSave,
  groupStart,
  groupEnd,
  border = true,
  featureFlag = false,
}: EventBoxProps) => {
  const [data, setCurrentData] = useState<SessionData>(() => {
    // if not initialData we are creating a new session
    // so in this case, we create an empty session
    return (
      initialData || {
        startsAtHour: '',
        endsAtHour: '',
        recurrent: false,
        frequency: undefined,
        reminders: [],
        trainingId: trainingId,
        trainingClassId: trainingClassId, // not used when creating a group. Required when creating a session for an existing group
        name: '',
        link: '',
        startDate: groupStart,
        modality: 'mixed',
        schedule: 'scheduled',
        sessionInstructors: {
          employeeSelection: {
            type: 'manual',
            ids: [],
          },
          sessionId: undefined,
          employeesData: [],
        },
      }
    )
  })

  const [showReminderModal, setShowReminderModal] = useState<boolean>(false)
  const [showStrategyModal, setShowStrategyModal] = useState<boolean>()
  const { validateLink } = useValidation()
  const companyId = useCurrentSession().company.id
  const relatorioEnabled = useIsRelatorioEnabled(companyId)

  const errorLink = validateLink(data.link)

  const isValid = () => {
    const hasValidName = Boolean(data.name) && !errorLink

    if (!hasValidName) return false

    if (data.schedule === 'scheduled') {
      const recurrentValid = !!data.dueDate === data.recurrent

      return Boolean(data.startsAt && data.endsAt && data.duration !== '0' && recurrentValid)
    }

    // selfpaced logic
    if (data.dueDate && data.startsAt) {
      return moment(data.dueDate).isSameOrAfter(moment(data.startsAt), 'day')
    }

    return true // valid if dueDate or startsAt is missing
  }

  const timeOptions = [
    { label: i18n.t('trainings.annex_c_relatorio_unico.during_working_hours'), value: '01' },
    { label: i18n.t('trainings.annex_c_relatorio_unico.outside_working_hours'), value: '02' },
    { label: i18n.t('trainings.annex_c_relatorio_unico.mixed_hours'), value: '03' },
  ]

  const scheduleOptions: { label: string; value: TrainingsSessionScheduleEnum }[] = [
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.type.scheduled'
      ),
      value: 'scheduled',
    },
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.type.self_paced'
      ),
      value: 'selfpaced',
    },
  ]

  const handleScheduleChange = (value: TrainingsSessionScheduleEnum | null) => {
    setCurrentData({
      ...data,
      startDate: '',
      startsAt: '',
      startsAtHour: '',
      endsAtHour: '',
      endsAt: '',
      hours: '',
      minutes: '',
      dueDate: '',
      duration: '',
      schedule: value,
      recurrent: false,
      sessionInstructors: {
        ...data.sessionInstructors,
        employeeSelection: data.sessionInstructors?.employeeSelection ?? {
          type: 'manual',
          ids: [],
        },
      },
    })
  }

  const { setDefaultTime } = useSessionsForm()
  const initialReminderData: ReminderValues = {
    name: '',
    scheduledAtUi: moment(setDefaultTime()).format('L'),
    scheduledAt: moment(setDefaultTime()).format('L'),
    scheduledAtHour: moment(setDefaultTime()).format('HH:mm'),
    timeframe: 'oneday',
  }

  const checkStrategyNecessary = () => {
    if (data.id && initialData?.recurrent) {
      setShowStrategyModal(true)

      return
    }

    onSave(data)
  }

  const [reminderData, setReminderData] = useState<ReminderValues>(initialReminderData)

  return (
    <Box
      border={
        border && {
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }
      }
      borderRadius={border ? { all: 'abs016' } : undefined}
      padding="s16"
      gap="s24"
    >
      <Box gap="s8">
        <Box gap="s8">
          <F0Select<TrainingsSessionScheduleEnum>
            label={i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.type.title'
            )}
            options={scheduleOptions}
            value={data.schedule ?? undefined}
            defaultItem={scheduleOptions[0]}
            onChange={(value: TrainingsSessionScheduleEnum | null) => handleScheduleChange(value)}
          />
        </Box>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween" gap="s16">
        <TextField
          value={data.name}
          type="text"
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.name'
          )}
          onChange={(name) => {
            setCurrentData({ ...data, name: name })
          }}
          mandatory
          maxLength={255}
        />
      </Box>
      {data.schedule === 'scheduled' ? (
        <ScheduledEvent
          setCurrentData={setCurrentData}
          data={data}
          groupStart={groupStart}
          groupEnd={groupEnd}
          featureFlag={featureFlag}
        />
      ) : (
        <SelfPacedEvent
          setCurrentData={setCurrentData}
          data={data}
          groupStart={groupStart}
          groupEnd={groupEnd}
        />
      )}
      <Box gap="s16" flexDirection="row">
        <TextField
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.link_title'
          )}
          value={data.link || ''}
          onChange={(value) => setCurrentData({ ...data, link: value })}
          error={errorLink}
          readonly={data.modality === 'inperson'}
        />
        <TextField
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.location_title'
          )}
          value={data.location || ''}
          onChange={(value) => setCurrentData({ ...data, location: value })}
          readonly={data.modality === 'online'}
          maxLength={255}
        />
      </Box>
      {relatorioEnabled && (
        <SelectField
          options={timeOptions}
          none={i18n.t('trainings.training_relatorio_fields.option_placeholder')}
          value={data.sessionRelatorioUnicoField?.trainingSchedule}
          label={i18n.t('trainings.annex_c_relatorio_unico.trainins_schedule')}
          onChange={(value) => {
            setCurrentData({ ...data, sessionRelatorioUnicoField: { trainingSchedule: value } })
          }}
        />
      )}
      <Box gap="s16">
        <Text size="200" weight="semibold" color="grey1000">
          {i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.reminder_title'
          )}
        </Text>
        <ReminderCard
          data={data}
          open={showReminderModal}
          setOpen={setShowReminderModal}
          setCurrentData={setCurrentData}
          reminderData={reminderData}
          setReminderData={setReminderData}
          initialReminderData={initialReminderData}
        />
      </Box>
      {data.schedule === 'scheduled' && (
        <CalendarInvitesCard data={data} setCurrentData={setCurrentData} />
      )}
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="flexEnd"
        gap="s8"
        paddingTop={'s24'}
      >
        <Button type="secondary" onClick={() => onClose()}>
          {i18n.t('common.cancel')}
        </Button>
        <Button onClick={() => checkStrategyNecessary()} disabled={!isValid()}>
          {i18n.t('common.save')}
        </Button>
      </Box>
      {showStrategyModal && (
        <SessionSelectStrategyModal
          title={i18n.t(
            'trainings.trainings_revamp.sessions.edit_session_modal.session_strategy_modal.title'
          )}
          onClose={() => setShowStrategyModal(false)}
          onSave={(strategy: TrainingsSessionUpdateStrategyEnum) => {
            onSave({ ...data, updateStrategy: strategy })
          }}
          saveText={i18n.t('common.save')}
          onlyAllSessions={initialData?.frequency !== data.frequency}
        />
      )}
    </Box>
  )
}
